import { HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AnalysisApiClientAccess } from '@nk/access-block';
import { GENERIC_AUTH_SERVICE_TOKEN, GenericAuthService } from '@nk/auth-lib';
import {
  AppFeatureKey,
  COUNTRY_FEATURES,
  SearchQueryMethod,
  SimpleSearchWithDistanceEntity,
} from '@nk/data-entities-lib';
import { getValidSearchQuery } from '@nk/utils-lib';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable()
export class CompanyPersonSearchService {
  constructor(
    private _analysisApiAccess: AnalysisApiClientAccess,
    @Inject(GENERIC_AUTH_SERVICE_TOKEN) private _authService: GenericAuthService,
  ) {}

  public search(
    query: string,
    limit: number,
    searchMode: SearchQueryMethod,
    includeDeadCompanies: boolean,
    geoCountryIdentifiers?: string[],
    onlyPersons = false,
    onlyCompanies = false, // if user is not logged in this option must be true
  ): Observable<SimpleSearchWithDistanceEntity[]> {
    return this._authService.authInfo$.pipe(
      switchMap((authInfo) => {
        // THis endpoint is available even if user is not logged in.
        if (onlyCompanies && onlyPersons) {
          throw new Error("Cannot be 'only person' and 'only companies'.");
        }
        if (!authInfo.isValid() && !onlyCompanies) {
          throw new Error('Search with persons is only available for users that logged in');
        }
        if (!authInfo.isValid() && Array.isArray(geoCountryIdentifiers) && geoCountryIdentifiers.length > 1) {
          throw new Error('Multiple countries are only available for users that logged in');
        }

        let endpointRoute: string;
        if (searchMode === SearchQueryMethod.quick && onlyCompanies) {
          endpointRoute = 'company-quick-search';
        } else if (searchMode === SearchQueryMethod.quick) {
          endpointRoute = 'quick-search';
        } else if (searchMode === SearchQueryMethod.fuzzy && onlyCompanies) {
          endpointRoute = 'company-fuzzy-search';
        } else {
          endpointRoute = 'fuzzy-search';
        }

        let params = new HttpParams();
        params = params.append('query', getValidSearchQuery(query, '\\$&'));
        params = params.append('limit', String(limit));
        params = params.append('include_dead_companies_flag', String(includeDeadCompanies));
        if (geoCountryIdentifiers && geoCountryIdentifiers.length > 0) {
          params = params.append('geo_country_identifiers', String(geoCountryIdentifiers));
        }
        if (onlyPersons) {
          params = params.append('only_person_flag', String(true));
        }

        return this._analysisApiAccess.get<SimpleSearchWithDistanceEntity[]>(
          endpointRoute,
          { params: params },
          authInfo.isValid() ? [AppFeatureKey.base, ...COUNTRY_FEATURES] : [],
        );
      }),
    );
  }
}
